<template>
  <div class="v2-product-gallery">
    <div class="v2-product-gallery__main">
      <div
        v-if="images.length > 1"
        class="v2-product-gallery__arrow v2-product-gallery__arrow--prev"
        @click="prev"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </div>
      <div class="v2-product-gallery__slide">
        <video
          v-if="currentImage && currentImage.includes('.mp4')"
          ref="videoRef"
          width="100%"
          height="100%"
          muted
          controls
          @mousemove="onHoverVideo"
          @mouseleave="onHoverVideo"
        >
          <source :src="currentImage">
          Video is not supported.
        </video>
        <img
          v-else
          :src="currentImage"
          :alt="`${productName} Gallery`"
          class="v2-product-gallery__image"
          @error="onImageError"
        >
      </div>
      <div
        v-if="images.length > 1"
        class="v2-product-gallery__arrow v2-product-gallery__arrow--next"
        @click="next"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </div>
    <div
      v-if="images.length > 1"
      class="v2-product-gallery__dots"
    >
      <button
        v-for="(img, idx) in images"
        :key="idx"
        class="v2-product-gallery__dot"
        :class="{ 'v2-product-gallery__dot--active': idx === activeIndex }"
        @click="goTo(idx)"
      />
    </div>
  </div>
</template>

<script>
import { getDefaultProductImage } from '@/@core/utils/utils'

export default {
  name: 'V2ProductGallery',
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    productName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      activeIndex: 0,
    }
  },
  computed: {
    currentImage() {
      return this.images[this.activeIndex] || ''
    },
  },
  methods: {
    prev() {
      this.activeIndex = this.activeIndex > 0
        ? this.activeIndex - 1
        : this.images.length - 1
    },
    next() {
      this.activeIndex = this.activeIndex < this.images.length - 1
        ? this.activeIndex + 1
        : 0
    },
    goTo(idx) {
      this.activeIndex = idx
    },
    onImageError(event) {
      event.target.src = getDefaultProductImage()
    },
    onHoverVideo(event) {
      if (event?.target?.paused) {
        event.target.play()
      } else {
        event.target.pause()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-product-gallery {
  width: 100%;

  &__main {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    aspect-ratio: 1;
    min-height: 300px;

    &:hover .v2-product-gallery__arrow {
      opacity: 1;
    }
  }

  &__slide {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  &__image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    object-position: center;
    display: block;
  }

  &__arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-full);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s ease;
    color: var(--v2-foreground);

    &:hover {
      background: #fff;
      box-shadow: var(--v2-shadow-sm);
    }

    &--prev {
      left: var(--v2-space-3);
    }

    &--next {
      right: var(--v2-space-3);
    }
  }

  &__dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-2);
    margin-top: var(--v2-space-3);
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: var(--v2-radius-full);
    background: var(--v2-border);
    border: none;
    padding: 0;
    cursor: pointer;
    transition: all 0.2s ease;

    &--active {
      background: var(--v2-primary);
      width: 24px;
    }

    &:hover:not(&--active) {
      background: var(--v2-muted-foreground);
    }
  }
}
</style>
